import { TRANSLATIONS } from '@/core/i18n/translations';
import type { AIWorkoutResponse, SportType, WorkoutSegment, ZoneDefinition } from '@/types';
import { useTpGarminBridge } from './useTpGarminBridge';
import { useWorkoutFileExport } from './useWorkoutFileExport';
import { useCloudCalendarExport } from './useCloudCalendarExport';

/**
 * Composes TP/Garmin bridge posting with downloadable/shareable workout files and markdown clipboard helpers.
 */
export function useWorkoutExport(
  generatedXml: string,
  generatedMarkdown: string,
  workoutName: string,
  language: 'en' | 'pl',
  isPro: boolean,
  setShowPricingModal: React.Dispatch<React.SetStateAction<boolean>>,
  segments: WorkoutSegment[],
  sport: SportType,
  storedFtp: number | null,
  activeZones?: ZoneDefinition[],
  aiWorkout?: AIWorkoutResponse | null,
) {
  const t = TRANSLATIONS[language];

  const { tpSending, garminSending, sendToTrainingPeaks, sendToGarminConnect } = useTpGarminBridge(
    generatedXml,
    workoutName,
    language,
    t,
    isPro,
    setShowPricingModal,
    sport,
    segments,
    activeZones,
  );

  const {
    copied,
    handleDownloadZwo,
    handleDownloadFit,
    handleShareWorkoutDescription,
    copyToClipboard,
  } = useWorkoutFileExport(
    generatedXml,
    generatedMarkdown,
    workoutName,
    language,
    t,
    isPro,
    setShowPricingModal,
    segments,
    sport,
    storedFtp ?? null,
  );

  const cloudCalendar = useCloudCalendarExport({
    t,
    language,
    workoutName,
    aiWorkout: aiWorkout ?? null,
    sport,
    segments,
    activeZones,
  });

  return {
    copied,
    tpSending,
    garminSending,
    sendToTrainingPeaks,
    sendToGarminConnect,
    handleDownloadZwo,
    handleDownloadFit,
    handleShareWorkoutDescription,
    copyToClipboard,
    ...cloudCalendar,
  };
}
